import { useForm } from "react-hook-form";
import PropTypes from "prop-types";
import DeleteButton from "components/buttons/DeleteButton";
import { Box, Grid, Typography } from "@mui/material";
import CancelButton from "components/buttons/CancelButton";
import SaveButton from "components/buttons/SaveButton";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CustomTextField from "components/inputs/CustomTextField";
import colors from "config/theme/colors";
import { useEffect, useState } from "react";
import { useIsMutating } from "@tanstack/react-query";
import DeleteModal from "components/modals/DeleteModal";
import { useGetAllEquipmentTypes } from "hooks/queries/useEquipmentType";
import SpinningCircle from "components/spinners/SpinningCircle";
import DropdownField from "components/inputs/DropdownField";
import ExitFormModal from "components/modals/ExitFormModal";

const EquipmentGroupForm = ({
  isCreateForm = false,
  selectedEquipmentGroup,
  isEditing = false,
  onSave,
  onCancel,
  onDelete,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    data: equipmentTypes,
    isLoading: equipmentTypesLoading,
    isError: equipmentTypesError,
  } = useGetAllEquipmentTypes();
  const isCreatingLoading =
    useIsMutating({
      mutationKey: ["createEquipmentGroup"],
    }) > 0;
  const isUpdateLoading =
    useIsMutating({
      mutationKey: ["updateEquipmentGroup"],
    }) > 0;
  const isDeleteLoading =
    useIsMutating({
      mutationKey: ["deleteEquipmentGroup"],
    }) > 0;

  const schema = yup.object({
    name: yup.string().required("Το πεδίο είναι υποχρεωτικό"),
    description: yup.string().nullable(),
    equipmentTypeId: yup
      .object()
      .shape({
        id: yup.number().required("Το πεδίο είναι υποχρεωτικό"),
        label: yup.string().required("Το πεδίο είναι υποχρεωτικό"),
      })
      .required("Το πεδίο είναι υποχρεωτικό"),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
    getValues,
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const defaultValues = selectedEquipmentGroup
      ? {
          name: selectedEquipmentGroup.name || "",
          description: selectedEquipmentGroup.description || "",
          equipmentTypeId: {
            id: selectedEquipmentGroup.equipmentType.id,
            label: selectedEquipmentGroup.equipmentType.name,
          },
        }
      : {
          name: "",
          description: "",
          equipmentTypeId: null,
        };
    reset(defaultValues);
  }, [selectedEquipmentGroup, equipmentTypes, reset]);

  if (equipmentTypesLoading) {
    return <SpinningCircle />;
  }

  if (equipmentTypesError) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
          height: "100%",
        }}
      >
        <p>Error: {equipmentTypesError.message}</p>
      </Box>
    );
  }

  const handleFormSubmit = (data) => {
    onSave(data);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const disabled = !isEditing && !isCreateForm;

  return (
    <>
      <form onSubmit={handleSubmit(() => handleFormSubmit(getValues()))}>
        <Box display="flex" flexDirection="column" gap={4}>
          <Typography fontSize={24} fontWeight={600} color={colors.secondary}>
            {isCreateForm
              ? "Δημιουργία Ομάδας"
              : `${selectedEquipmentGroup?.name} - ${isEditing ? "Επεξεργασία" : "Προβολή"}`}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CustomTextField
                name="name"
                label="Όνομα"
                type="text"
                value={selectedEquipmentGroup?.name}
                control={control}
                disabled={disabled}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <CustomTextField
                name="description"
                label="Περιγραφή"
                type="textarea"
                value={selectedEquipmentGroup?.description}
                control={control}
                disabled={disabled}
              />
            </Grid>

            <Grid item xs={12}>
              <DropdownField
                name="equipmentTypeId"
                label="Τύπος Εξοπλισμού"
                required
                control={control}
                disabled={disabled}
                editable={isCreateForm}
                value={selectedEquipmentGroup?.equipmentType}
                options={equipmentTypes?.items?.map((type) => ({
                  id: type.id,
                  label: type.name,
                }))}
              />
            </Grid>
          </Grid>

          {Object.keys(errors).length > 0 && (
            <Typography color="error" fontSize={14}>
              Παρακαλώ ελέγξτε ότι όλα τα πεδία έχουν συμπληρωθεί σωστά
            </Typography>
          )}

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <Box display="flex" gap={2} justifyContent={"flex-start"}>
              {isCreateForm && isDirty && (
                <CancelButton
                  onClick={() => {
                    reset();
                    onCancel();
                  }}
                  disabled={disabled}
                />
              )}
              {!isCreateForm && (
                <CancelButton
                  onClick={() => {
                    reset();
                    onCancel();
                  }}
                />
              )}
              {!isCreateForm && isEditing && (
                <DeleteButton
                  onClick={handleOpenModal}
                  text="Διαγραφή Ομάδας"
                  disabled={disabled}
                  isLoading={isDeleteLoading}
                />
              )}
            </Box>

            {(isEditing || isCreateForm) && (
              <SaveButton
                disabled={disabled || !isDirty}
                text={isCreateForm ? "Δημιουργία" : "Αποθήκευση"}
                isLoading={isUpdateLoading || isCreatingLoading}
              />
            )}

            {isEditing && (
              <DeleteModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onDelete={onDelete}
                itemTitle={selectedEquipmentGroup?.name}
                title="Πρόκειται να διαγράψετε την ομάδα"
                description="Σιγουρευτείτε ότι η συγκεκριμένη ομάδα δεν χρησιμοποιείται σε κάποια κλάση εξοπλισμού. Είστε βέβαιοι ότι θέλετε να προχωρήσετε στη διαγραφή της ομάδας;"
              />
            )}
          </Box>
        </Box>
      </form>

      <ExitFormModal isFormDirty={isDirty} />
    </>
  );
};

EquipmentGroupForm.propTypes = {
  isCreateForm: PropTypes.bool,
  selectedEquipmentGroup: PropTypes.object,
  isEditing: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

export default EquipmentGroupForm;
